import { Navigate } from 'react-router-dom';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { ActionCenterV2 } from '@/components/action-center/action-center-v2';
import { ROUTES, urls } from '@/urls';
import DisputesTable from '@/components/disputes-table/disputes-table';
import CostPerDay from '@/components/shared-charts/cost-per-day-chart';
import { DsIcon } from '@/components/ds';
import 'intro.js/introjs.css';
import { getToken, getUser } from '@/lib/common';
import { AppCard } from '@/components/app-card/app-card';
import AppCardContainer from '@/components/app-card/app-card-container';
import { AppContainer } from '@/components/ui';
import CostsSummary from './components/costs-summary';
import { useActionCenterViewContext } from '@/contexts/action-center-view-context';
import { ActionCenterItemV2 } from '@cloudcost/shared-types/action-center-v2';
import { ActionCenterModule, fetchActionCenterItemV2 } from '@/utils/actionCenterDetails';

const TYPE_TO_PATH: Record<ActionCenterItemV2['action_type'], string> = {
  Allocation: ROUTES.ALLOCATION,
  Budget: ROUTES.BUDGETS,
  Budgets: ROUTES.BUDGETS,
  Attestation: ROUTES.ATTESTATION,
  Optimization: ROUTES.OPTIMIZATION,
  Recommendation: ROUTES.OPTIMIZATION,
};

function mapActionTypeToModule(
  actionType: ActionCenterItemV2['action_type'],
): ActionCenterModule {
  switch (actionType) {
    case 'Attestation':
      return 'Attestation';
    case 'Allocation':
      return 'Allocation';
    case 'Budget':
    case 'Budgets':
      return 'Budgets';
    case 'Recommendation':
    case 'Optimization':
      return 'Optimization';
    default:
      return 'Optimization';
  }
}

function About() {
  const user = getUser();
  const { triggerView } = useActionCenterViewContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [dashboardItems, setDashboardItems] = useState<ActionCenterItemV2[]>([]);
  const processedActionItemIdRef = useRef<string | null>(null);
  const isProcessingRef = useRef<boolean>(false);
  
  // Store latest values in refs to avoid dependency issues
  const actionItemIdRef = useRef<string | null>(null);
  const triggerViewRef = useRef(triggerView);
  const navigateRef = useRef(navigate);
  const locationRef = useRef(location);
  const setSearchParamsRef = useRef(setSearchParams);
  
  // Update refs when values change
  useEffect(() => {
    triggerViewRef.current = triggerView;
    navigateRef.current = navigate;
    locationRef.current = location;
    setSearchParamsRef.current = setSearchParams;
  }, [triggerView, navigate, location, setSearchParams]);
  
  // Get action_item ID from URL - memoize to prevent unnecessary re-renders
  const actionItemId = useMemo(() => searchParams.get('action_item'), [searchParams]);
  
  // Update actionItemId ref
  useEffect(() => {
    actionItemIdRef.current = actionItemId;
  }, [actionItemId]);

  // Process action item when items are loaded - using refs to avoid recreation
  const processActionItem = useCallback(async (items: ActionCenterItemV2[], currentActionItemId: string | null) => {
    // Skip if no action_item, no items, already processing, or already processed this ID
    if (!currentActionItemId || items.length === 0 || isProcessingRef.current || processedActionItemIdRef.current === currentActionItemId) {
      return;
    }

    // Mark as processing and processed immediately to prevent duplicate processing
    isProcessingRef.current = true;
    processedActionItemIdRef.current = currentActionItemId;

    const processItem = (item: ActionCenterItemV2) => {
      const targetPath = TYPE_TO_PATH[item.action_type] || locationRef.current.pathname;
      const targetModule = mapActionTypeToModule(item.action_type);

      // Set the selection first - this persists to localStorage
      triggerViewRef.current({
        module: targetModule,
        item: item,
        originPath: locationRef.current.pathname,
        targetPath,
      });

      // Clear the query parameter and navigate
      setSearchParamsRef.current((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('action_item');
        return newParams;
      }, { replace: true });

      // Navigate to the appropriate screen
      if (targetPath !== locationRef.current.pathname) {
        setTimeout(() => {
          navigateRef.current(targetPath);
        }, 0);
      }

      // Reset processing flag after a short delay
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 100);
    };

    // First, try to find in dashboard items
    const itemInDashboard = items.find(
      (it) => String(it.id) === String(currentActionItemId),
    );

    if (itemInDashboard) {
      processItem(itemInDashboard);
      return;
    }

    // If not found in dashboard items, try to fetch from all modules
    const modules: ActionCenterModule[] = [
      'Optimization',
      'Budgets',
      'Allocation',
      'Attestation',
    ];

    try {
      const results = await Promise.all(
        modules.map(async (module) => {
          try {
            const item = await fetchActionCenterItemV2(module, currentActionItemId);
            return item;
          } catch {
            return undefined;
          }
        }),
      );

      const foundItem = results.find((item) => item !== undefined);
      if (foundItem) {
        processItem(foundItem);
      } else {
        // Item not found in any module, clear the parameter
        setSearchParamsRef.current((prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete('action_item');
          return newParams;
        }, { replace: true });
        processedActionItemIdRef.current = null;
        isProcessingRef.current = false;
      }
    } catch (error) {
      // On error, reset processing flag
      isProcessingRef.current = false;
      processedActionItemIdRef.current = null;
    }
  }, []); // No dependencies - uses refs

  // Reset processed ID when action_item parameter is removed
  useEffect(() => {
    if (!actionItemId) {
      processedActionItemIdRef.current = null;
      isProcessingRef.current = false;
    }
  }, [actionItemId]);

  // Stable callback that never changes - uses refs internally
  const handleItemsLoaded = useCallback((items: ActionCenterItemV2[]) => {
    setDashboardItems(items);
    // Process action item immediately when items are loaded, using current actionItemId from ref
    const currentActionItemId = actionItemIdRef.current;
    if (currentActionItemId && items.length > 0) {
      processActionItem(items, currentActionItemId);
    }
  }, [processActionItem]); // Only depends on processActionItem which is now stable

  if (!getToken()) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return (
    <AppContainer>
      {(user?.is_sponsor || user?.is_admin) && <CostsSummary />}
        <>
          <ActionCenterV2
            endpointUrl={urls.DevOps.getDashboardActionCenterV2}
            onItemsLoaded={handleItemsLoaded}
          />
        </>

      <AppCardContainer>
        <CostPerDay
          className="card100"
          url={urls.DevOps.getCostPerDayDashboard}
          title="Spend over time"
          subtitle="Current month vs. forecast, budget & last month"
        />
      </AppCardContainer>

      <AppCardContainer>
        <AppCard
          className="card100"
          title="Disputes Center"
          intro="Disputes Center is where all discussions take place. You can ask your DevOps team why allocation is not performed, or owners off costs why the Attestation is overdue. <br /> IT should also be used to discuss the changes in Budget values, progress on implementing recommendations and for explanations of costs. <br /> You can open the dispute by clicking the topic in Action Center, and you cann add additional users to the discussion as needed."
          icon={<DsIcon name="chat" size={18} />}>
          <DisputesTable Filter="" />
        </AppCard>
      </AppCardContainer>
    </AppContainer>
  );
}

export default About;
